import { ref, reactive, onActivated, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {  MsgBox } from '@/util/msgutil/msgBox';
import { Plus, Edit, Delete } from '@element-plus/icons-vue'
import { request } from '@/util/globalContext';

export default function enumList() {
    // ========== 响应式数据定义 ==========
    // 树数据（注：当前枚举功能未使用树组件，该部分为冗余数据）
    const treeData = ref([])
    const checkedRoleKeys = ref([])

    // 搜索表单（默认值初始化）
    const searchForm = reactive({
        enumType: '',
        enumName: '',
        code: '',
        isDeleted: ''
    })

    // 列表及弹窗核心响应式数据
    const listRef = ref(null) // 列表组件引用
    const dialogVisible = ref(false) // 新增/编辑弹窗显示状态
    const dialogType = ref('add') // 弹窗类型：add-新增 edit-编辑
    const formRef = ref(null) // 表单组件引用
    const editShow = ref(null) // 修改按钮显示控制
    const delShow = ref(null) // 删除按钮显示控制

    // 弹出表单默认值
    const FORM_DEFAULT = {
        enumType: '',
        enumName: '',
        dynamicRows: [] // 动态行默认值
    }
    const formData = reactive({ ...FORM_DEFAULT }) // 表单绑定数据

    // 表单校验规则
    const formRules = reactive({
        enumType: [
            { required: true, message: '请输入枚举', trigger: 'blur' },
            { max: 50, message: '内容超出指定长度', trigger: 'blur' }
        ],
        enumName: [
            { required: true, message: '请输入枚举描述', trigger: 'blur' },
            { max: 100, message: '内容超出指定长度', trigger: 'blur' }
        ],
        dynamicRows: [
            {
                required: true,
                type: 'array',
                message: '至少添加一条枚举值配置',
                trigger: 'change'
            },
            {
                validator: (rule, value, callback) => {
                    // 遍历动态行，逐个校验子字段
                    if (Array.isArray(value)) {
                        let error = null;
                        value.forEach((row, index) => {
                            if(value.length > 100){
                                error = new Error(`单次最多新增100条`);
                                return callback(error);
                            }
                            // 校验 enumCode（必填）
                            if (!row.enumCode?.trim()) {
                                error = new Error(`第${index+1}行：请输入枚举值`);
                                return callback(error);
                            }
                            if (row.enumCode.length > 50) {
                                error = new Error(`第${index+1}行：枚举值超出50字符限制`);
                                return callback(error);
                            }
                            // 校验 enumCodeName（必填）
                            if (!row.enumCodeName?.trim()) {
                                error = new Error(`第${index+1}行：请输入值描述`);
                                return callback(error);
                            }
                            if (row.enumCodeName.length > 100) {
                                error = new Error(`第${index+1}行：值描述超出100字符限制`);
                                return callback(error);
                            }
                            // 校验 code（必填）
                            if (!row.code?.trim()) {
                                error = new Error(`第${index+1}行：请输入显示值`);
                                return callback(error);
                            }
                            if (row.code.length > 50) {
                                error = new Error(`第${index+1}行：显示值超出50字符限制`);
                                return callback(error);
                            }
                            // 校验 isDeleted（必填）
                            if (row.isDeleted === undefined || row.isDeleted === '') {
                                error = new Error(`第${index+1}行：请选择是否生效`);
                                return callback(error);
                            }
                            // groupCode/groupName 非必填，仅校验长度
                            if (row.groupCode && row.groupCode.length > 50) {
                                error = new Error(`第${index+1}行：分组编码超出50字符限制`);
                                return callback(error);
                            }
                            if (row.groupName && row.groupName.length > 100) {
                                error = new Error(`第${index+1}行：分组描述超出100字符限制`);
                                return callback(error);
                            }
                            if(row.groupCode != row.groupName){
                                error = new Error(`第${index+1}行：分组信息不全`);
                                return callback(error);
                            }
                        });
                        callback(); // 所有行校验通过
                    } else {
                        callback(new Error('枚举值配置格式错误'));
                    }
                },
                trigger: 'blur,change'
            }
        ]
    })

    // ========== 核心方法 - 列表加载/重置 ==========
    // 加载枚举列表（含分页、搜索参数）
    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        // 校验列表组件初始化状态
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)
        try {
            // 组装请求参数（分页+搜索）
            const params = {
                ...paginationParams,
                ...searchForm
            }
            const res = await request.get('/enum/getEnumList', { params })
            if (res.code === 200) {
                // 更新列表数据和分页信息
                listRef.value.updateListData(res.data.records || [])
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 重置搜索表单
    const handleReset = () => {
        Object.keys(searchForm).forEach((key) => {
            searchForm[key] = Array.isArray(searchForm[key]) ? [] : ''
        })
    }

    // ========== 列表交互 - 行选择 ==========
    // 列表勾选行变化
    const handleSelectionChange = (val) => {
        delShow.value = val?.length ? val : null

        // 1. 如果没有选中任何行，直接置空
        if (!val?.length) {
            editShow.value = null;
            return;
        }
        // 2. 提取第一行的 enumType 作为基准值
        const base = val[0].enumType;

        // 3. 校验所有行的 enumType 是否与基准值一致
        const isAll = val.every(row => {
            // 兼容 undefined/null 的情况，统一转为空字符串比较
            return (row.enumType || '') === (base || '');
        });
        // 4. 全部相同则赋值，否则置空
        editShow.value = isAll ? val : null;
    }

    // 列表当前行变化
    const handleCurrentRowChange = (val) => {
        delShow.value = val
    }

    // ========== 列表按钮操作 - 新增/编辑/删除 ==========
    // 新增枚举（重置表单+打开弹窗）
    const handleAdd = () => {
        dialogType.value = 'add'
        formRef.value?.resetFields() // 清空表单校验状态
        // 深拷贝 FORM_DEFAULT，避免引用类型污染
        Object.assign(formData, JSON.parse(JSON.stringify(FORM_DEFAULT)))
        dialogVisible.value = true
    }

    // 编辑枚举（获取选中行详情+打开弹窗）
    const handleEdit = async () => {
        if (!editShow.value) {
            ElMessage.warning('请先选中数据！')
            return
        }
        if (!listRef.value?.isInited) {
            ElMessage.warning('列表组件初始化中，请稍后重试')
            return
        }

        try {
            listRef.value.setLoading(true)
            const ids = editShow.value?.map(row => row.id) || [];
            const editData = { ids: ids };
            const res = await request.post('/enum/getEditList', { ...editData })

            if (res.code !== 200) {
                ElMessage.error(res.msg || '接口异常')
                return
            }
            // 赋值表单数据
            const roleData = res.data.records
            formData.enumType = roleData[0].enumType
            formData.enumName = roleData[0].enumName
            formData.dynamicRows =  roleData.map(item => ({
                    id: item.id,
                    enumCode: item.enumCode || '',
                    enumCodeName: item.enumCodeName,
                    code: item.code,
                    groupCode: item.groupCode,
                    groupName: item.groupName,
                    isDeleted: item.isDeleted
                }));
            dialogType.value = 'edit'
            dialogVisible.value = true
        } catch (error) {
            ElMessage.error('获取详情失败' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 删除枚举（确认弹窗+接口调用）
    const handleDelete = async () => {
        if (!delShow.value) {
            ElMessage.warning('请先选中数据！')
            return
        }
        try {
            const hasNotDeletedData = delShow.value.some(row => row.isDeleted === 0);
            if (hasNotDeletedData) {
                ElMessage.warning('包含有效状态的数据，无法删除！');
                return;
            }
            // 确认弹窗
            await ElMessageBox.confirm(
                '确定删除？',
                '删除确认',
                { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
            )

            listRef.value.setLoading(true)
            const ids = delShow.value?.map(row => row.id) || [];
            const delData = { ids: ids };
            const res = await request.post('/enum/delEnums', { ...delData })
            if (res.code === 200) {
                ElMessage.success('删除成功')
                // 刷新列表
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                MsgBox.warning2('删除失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            // 排除取消弹窗的异常
            if (error !== 'cancel') {
                ElMessage.error('删除失败：网络异常')
            }
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 弹窗操作 - 提交 ==========
    // 提交新增/编辑枚举表单（校验+接口调用）
    const handleSubmit = async () => {
        if (!formRef.value) return

        // 表单校验（返回布尔值）
        const isValid = await new Promise((resolve) => {
            formRef.value.validate((valid) => {
                resolve(valid);
            });
        });
        if (!isValid) return;

        // ========== 组装目标数据结构 ==========
        const submitData = {
            enumType: formData.enumType,    // 固定字段：枚举
            enumName: formData.enumName,    // 固定字段：枚举描述
            list: formData.dynamicRows ,     // 动态行列表（addDynamicRow 添加的数据）
            dialogType: dialogType.value
        };
        if (submitData.list.length === 0) {
            ElMessage.warning('请至少添加一条枚举值数据！');
            return;
        }
        listRef.value.setLoading(true)
        try {
            // 注：当前接口是用户接口，需替换为枚举保存/更新接口
            const res = await request.post('/enum/saveOrUpdateEnums', { ...submitData })
            if (res.code === 200) {
                ElMessage.success(res.msg)
                dialogVisible.value = false // 关闭弹窗
                // 刷新列表
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                const tip = dialogType.value === 'add' ? '新增失败' : '修改失败'
                ElMessage.error(tip + '：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('接口异常:' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ========== 弹窗动态行操作 - 新增/删除 ==========
    // 新增动态行
    const addDynamicRow = () => {
        formData.dynamicRows.push({
            enumCode: '',
            enumCodeName: '',
            groupCode: '',
            groupName: '',
            isDeleted: 0 // 默认生效
        })
    }

    // 删除动态行
    const removeDynamicRow = (index) => {
        formData.dynamicRows.splice(index, 1);
    }


    // ========== 生命周期 ==========
    // 挂载时加载列表
    onMounted(async () => {
        await nextTick()
        setTimeout(() => {
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        }, 50)
    })

    // 激活时刷新列表（keep-alive场景）
    onActivated(() => {
        if (listRef.value?.isInited) {
            const pagination = listRef.value.getPagination()
            handleLoadList(pagination)
        }
    })

    // ========== 暴露变量/方法 ==========
    return {
        // 响应式数据
        searchForm,
        listRef,
        dialogType,
        formRef,
        editShow,
        delShow,
        formData,
        formRules,
        // 图标
        Plus, Edit, Delete,
        // 列表交互
        handleSelectionChange,
        handleCurrentRowChange,
        // 列表操作
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleLoadList,
        handleReset,
        // 动态行操作
        addDynamicRow,
        removeDynamicRow,
        // 弹窗状态
        dialogVisible,
        // 树相关（冗余）
        treeData,
        checkedRoleKeys
    }
}